import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TenderHelperService {

  constructor() { }

  // Extract array from API response
  extractArrayFromResponse(response: any): any[] {
    if (response?.data && Array.isArray(response.data)) return response.data;
    if (Array.isArray(response)) return response;
    return [];
  }

  // Get property value with multiple key variations
  getPropertyValue(obj: any, ...keys: string[]): string {
    for (const key of keys) {
      const value = obj?.[key] || obj?.[key.charAt(0).toUpperCase() + key.slice(1)];
      if (value) return String(value).trim();
    }
    return '';
  }

  // Get trimmed value
  getTrimmedValue(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  // Format date for display in input type="date" (YYYY-MM-DD)
  formatDateForDisplay(dateValue: any): string {
    if (!dateValue) return '';
    
    if (typeof dateValue === 'string') {
      // If it's an ISO string (contains 'T'), extract YYYY-MM-DD part
      if (dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      // If it's already in YYYY-MM-DD format, return as is
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateValue;
      }
      // Try to parse and format
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return dateValue;
    }
    
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return '';
      return dateValue.toISOString().split('T')[0];
    }
    
    return String(dateValue);
  }

  // Format date and time for display (YYYY-MM-DD HH:MM)
  formatDateTimeForDisplay(dateValue: any): string {
    if (!dateValue) return '';
    
    let date: Date | null = null;
    
    if (typeof dateValue === 'string') {
      // Try to parse the date string
      date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue;
    } else if (dateValue instanceof Date) {
      date = dateValue;
      if (isNaN(date.getTime())) return '';
    } else {
      return String(dateValue);
    }
    
    if (!date) return '';
    
    // Format as YYYY-MM-DD HH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  // Format date for sending to API (ISO string or null)
  formatDateForAPI(dateValue: any): string | null {
    if (!dateValue) return null;
    
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (trimmed === '') return null;
      
      // If it's already an ISO string, return as is
      if (trimmed.includes('T')) {
        const date = new Date(trimmed);
        if (!isNaN(date.getTime())) {
          return trimmed;
        }
        return null;
      }
      
      // If it's a date string (YYYY-MM-DD) from input type="date", convert to ISO
      if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const date = new Date(trimmed + 'T00:00:00');
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
        return null;
      }
      
      // Try to parse as date
      const parsedDate = new Date(trimmed);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString();
      }
      
      return null;
    }
    
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return null;
      return dateValue.toISOString();
    }
    
    return null;
  }

  // Parse number
  parseNumber(value: any): number {
    return value ? parseFloat(String(value)) || 0 : 0;
  }

  // Normalize boolean string
  normalizeBooleanString(value: any): boolean {
    if (value === null || value === undefined || value === '') return false;
    return (value === 'true' || value === true);
  }

  // Convert Yes/No to True/False
  convertYesNoToTrueFalse(value: any): boolean | null {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }
    const stringValue = String(value).trim().toLowerCase();
    if (stringValue === 'yes' || stringValue === 'true') return true;
    if (stringValue === 'no' || stringValue === 'false') return false;
    return null;
  }

  // Convert boolean to Yes/No
  convertBooleanToYesNo(value: any): string {
    if (value === null || value === undefined) return '';
    const stringValue = String(value).toLowerCase().trim();
    if (stringValue === 'true' || value === true) return 'Yes';
    if (stringValue === 'false' || value === false) return 'No';
    return '';
  }

  // Get valid value from API data
  getValidValue(
    inputValue: any,
    apiData: any[],
    propertyKeys: string[],
    getPropertyValueFn: (obj: any, ...keys: string[]) => string
  ): string | null {
    if (!inputValue || typeof inputValue !== 'string') return null;
    
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || apiData.length === 0) return null;
    
    let matchedValue: string | null = null;
    apiData.find(item => {
      const value = getPropertyValueFn(item, ...propertyKeys);
      if (value.toLowerCase() === trimmedValue.toLowerCase()) {
        matchedValue = value;
        return true;
      }
      return false;
    });
    
    return matchedValue;
  }

  // Check if response is successful
  isSuccessResponse(response: any): boolean {
    return response && (response.IsSuccess || response.isSuccess || response.data);
  }

  // Get error message from response
  getResponseErrorMessage(response: any, defaultMessage: string): string {
    return response?.ErrorMessageEn || 
           response?.errorMessageEn || 
           response?.message || 
           defaultMessage;
  }

  // Get error message from error object
  getErrorMessage(error: any): string {
    return error?.error?.ErrorMessageEn || 
           error?.error?.errorMessageEn || 
           error?.error?.message || 
           error?.message || '';
  }

  // Extract email from contact details
  extractEmail(contactDetails: string): string {
    if (!contactDetails) return '';
    const emailMatch = contactDetails.match(/[\w\.-]+@[\w\.-]+\.\w+/);
    return emailMatch ? emailMatch[0] : '';
  }

  // Extract phone from contact details
  extractPhone(contactDetails: string): string {
    if (!contactDetails) return '';
    const phoneMatch = contactDetails.match(/[\d\s\+\-\(\)]+/);
    return phoneMatch ? phoneMatch[0].trim() : '';
  }
}

